import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearBloodGlucoseEntries,
    deleteBloodGlucose,
    loadBloodGlucoseEntries
} from '../../../../store/participantService';
import { scrollToFocus } from '../../../../utils/eventUtils';
import { DEFAULT_TABLE_ROWS_PER_PAGE } from '../../../../utils/formValidation/commonConstants';
import {
    BLOOD_GLUCOSE_ERROR,
    MEASUREMENT_TYPES_ERROR
} from '../../../../utils/formValidation/loadingErrorMessageConstants';
import { stableSort } from '../../../../utils/sortFunctions';
import Button from '../../../formElements/Button';
import ResultsTable from '../../../table/ResultsTable';
import LoadingSpinner from '../../../ui/LoadingSpinner';

import BloodGlucoseRow from './BloodGlucoseRow';
import EditBloodGlucose from './EditBloodGlucose';
import { HEALTH_INCIDENT_LEVEL } from './validateBloodGlucose';

import app from '../../../../app.module.css';
import local from '../wellbeing.module.css';

const headCells = [
    { id: 'eventDate', numeric: false, label: 'Date' },
    { id: 'measurementValue', numeric: true, label: 'Glucose Value' },
    { id: 'measurement', numeric: false, label: 'Measurement' },
    { id: 'isWarning', numeric: false, label: 'Health Incident Required' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'eventDate',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const BloodGlucose = ({
    participant,
    isEditDisabled = true,
    isDeleteDisabled = true,
    isCreateDisabled = true,
    isAuditable,
    onSearchAuditItems
}) => {
    const dispatch = useDispatch();
    const bloodGlucoseRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [loadingContentMessage, setLoadingContentMessage] = useState('');
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );
    const bloodGlucoseEntries = useSelector(
        (state) => state.entities.participantService.bloodGlucoseEntries
    );
    const bloodGlucoseEntriesMetaData = useSelector(
        (state) => state.entities.participantService.bloodGlucoseEntriesMetaData
    );

    // USEEFFECTS
    useEffect(() => {
        return () => cleanUp();
    }, []);

    useEffect(() => {
        participant?.id && dispatch(loadBloodGlucoseEntries(participant.id));
    }, [participant?.id]);

    useEffect(() => {
        if (measurementTypes?.length < 1) return;
        if (bloodGlucoseEntriesMetaData?.loaded) {
            if (!bloodGlucoseEntries?.length) {
                rows.length && setRows([]);
                setLoadingContentMessage(BLOOD_GLUCOSE_ERROR);
            } else {
                const rows = bloodGlucoseEntries.map((el) => {
                    const measurementType =
                        measurementTypes.find((entry) => entry.id === el.measurementId)?.name || '';
                    return {
                        ...el,
                        measurement: measurementType,
                        isWarning: el.measurementValue > HEALTH_INCIDENT_LEVEL
                    };
                });
                setRows(rows);
            }
        }
    }, [bloodGlucoseEntries, bloodGlucoseEntriesMetaData, measurementTypes]);

    // FNs
    const loadingError = () => {
        if (measurementTypes?.length < 1) return MEASUREMENT_TYPES_ERROR;
    };

    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <BloodGlucoseRow
                key={el.id}
                row={el}
                isDisabled={isEditDisabled || showForm}
                isDeleteDisabled={isDeleteDisabled || showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                isAuditable={isAuditable}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const cleanUp = () => {
        dispatch(clearBloodGlucoseEntries());
    };

    // EVENT HANDLERS
    const onCreate = () => {
        setFormType('create');
        setSelectedRow({});
        setShowForm(true);
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setFormType('edit');
        setSelectedRow(row);
        setShowForm(true);
    };

    const onDelete = (row) => {
        setSelectedRow(row);
        dispatch(deleteBloodGlucose(row.id));
    };

    const onClose = () => {
        setShowForm(false);
        setSelectedRow({});
        scrollToFocus(bloodGlucoseRef);
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg)
        return (
            <div className={`${local.wellBeingSection} ${app.fadein}`}>
                <LoadingSpinner content={errorMsg} />
            </div>
        );

    return (
        <div
            className={`${local.wellBeingSection} ${app.fadein}`}
            data-testid="blood_glucose"
            ref={bloodGlucoseRef}>
            <h2 className={app.sectionHeading}>Blood Glucose</h2>
            <div className={local.tableSection}>
                {rows.length < 1 ? (
                    <LoadingSpinner content={loadingContentMessage} />
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        defaultRowsPerPage={rowMetaData.rowsPerPage}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm ? (
                    <Button
                        id="addBloodGlucoseButton"
                        type="button"
                        content="Add New Blood Glucose"
                        disabled={isCreateDisabled}
                        icon={true}
                        onClick={onCreate}
                    />
                ) : (
                    <EditBloodGlucose
                        participant={participant}
                        onClose={onClose}
                        isDisabled={
                            (formType === 'edit' && isEditDisabled) ||
                            (formType === 'create' && isCreateDisabled)
                        }
                        formType={formType}
                        row={selectedRow}
                    />
                )}
            </div>
        </div>
    );
};

BloodGlucose.propTypes = {
    participant: PropTypes.object.isRequired,
    isEditDisabled: PropTypes.bool,
    isDeleteDisabled: PropTypes.bool,
    isCreateDisabled: PropTypes.bool,
    isAuditable: PropTypes.bool,
    onSearchAuditItems: PropTypes.func
};

export default BloodGlucose;
